import {
  type DocumentData,
  type Firestore,
  collection,
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ContactSchema, type Contact } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Depuis le chantier `contact-type`, `contact` porte le discriminant `type`
 * comme les 55 autres familles (`person` | `organization`, vocabulaire ferme,
 * verrouille par les Security Rules). `list({ type })` et `count({ type })`
 * filtrent donc COTE SERVEUR. Piege de migration a connaitre : un document
 * cree avant ce champ n'est PAS rattrape par `where('type', '==', ...)` tant
 * que `scripts/backfill-contact-type.mjs` n'a pas pose la valeur en base.
 */
type ContactListOptions = ListOptions;
type ContactCountOptions = CountOptions;

/**
 * Champs standard et générés par le système : l'appelant ne les fournit jamais
 * à la création, le SDK les remplit automatiquement.
 */
export type ContactCreateInput = Omit<
  Contact,
  'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'archivedAt' | 'deletedAt' | 'schemaVersion'
>;

/**
 * Patch de mise à jour d'un contact (update partiel).
 * `orgId` et `type` sont immuables (Security Rules : on ne transforme pas une
 * personne en organisation, on crée l'organisation) ; les champs standard sont
 * gérés par le SDK (`updatedAt`/`updatedBy` remplis automatiquement) : aucun
 * des trois n'est accepté ici.
 */
type ContactUpdateInputStrict = Partial<
  Omit<
    Contact,
    | 'id'
    | 'orgId'
    | 'type'
    | 'createdAt'
    | 'updatedAt'
    | 'createdBy'
    | 'updatedBy'
    | 'archivedAt'
    | 'deletedAt'
    | 'schemaVersion'
  >
>;

/** `null` efface un champ natif facultatif, comme une case videe dans la fiche. */
export type ContactUpdateInput = Omit<ContactUpdateInputStrict, 'name' | 'firstName' | 'lastName' | 'email' | 'phone'> & {
  name?: Contact['name'] | null;
  firstName?: Contact['firstName'] | null;
  lastName?: Contact['lastName'] | null;
  email?: Contact['email'] | null;
  phone?: Contact['phone'] | null;
};

/** Nom de la collection Firestore des contacts (entité cœur, org-wide). */
export const CONTACTS_COLLECTION = 'contacts';

function customFieldsApresPatch(
  actuel: Contact['customFields'],
  patch: ContactUpdateInput['customFields'],
): Contact['customFields'] {
  if (patch === undefined) return actuel;
  const fusion = { ...actuel };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) delete fusion[key];
    else if (value !== undefined) fusion[key] = value;
  }
  return Object.keys(fusion).length === 0 ? undefined : fusion;
}

/** Projection canonique validee AVANT la creation Firestore. */
export function validateContactCreate(
  id: string,
  input: ContactCreateInput,
  actor: string,
  now: Date,
): Contact {
  return ContactSchema.parse({
    ...input,
    id,
    createdAt: now,
    updatedAt: now,
    createdBy: actor,
    updatedBy: actor,
    archivedAt: null,
    deletedAt: null,
    schemaVersion: CURRENT_SCHEMA_VERSION,
  });
}

/** Projection canonique d'un patch, sans perdre les champs absents du patch. */
export function validateContactUpdate(
  actuel: Contact,
  patch: ContactUpdateInput,
  actor: string,
  now: Date,
): Contact {
  const metier = sanitizeContactUpdate(patch);
  const projection: Record<string, unknown> = {
    ...actuel,
    ...metier,
    customFields: customFieldsApresPatch(actuel.customFields, patch.customFields),
    updatedAt: now,
    updatedBy: actor,
  };
  for (const key of ['name', 'firstName', 'lastName', 'email', 'phone'] as const) {
    if (metier[key] === null) delete projection[key];
  }
  return ContactSchema.parse(projection);
}

/** Retire les cles hors contrat qu'un appel JavaScript pourrait fournir. */
export function sanitizeContactUpdate(patch: ContactUpdateInput): ContactUpdateInput {
  const out: ContactUpdateInput = {};
  for (const key of ['name', 'firstName', 'lastName', 'email', 'phone'] as const) {
    const value = patch[key];
    if (value !== undefined) out[key] = value;
  }
  if (patch.customFields !== undefined) out.customFields = patch.customFields;
  // Couleur et photo de reference (`null` : retirees), champs du socle de toute fiche.
  if (patch.couleur !== undefined) out.couleur = patch.couleur;
  if (patch.photo !== undefined) out.photo = patch.photo;
  return out;
}

function buildContactUpdateData(patch: ContactUpdateInput): DocumentData {
  const data = buildUpdateData(patch);
  for (const key of ['name', 'firstName', 'lastName', 'email', 'phone'] as const) {
    if (patch[key] === null) data[key] = deleteField();
  }
  return data;
}

/**
 * Repository typé pour l'entité `contact`.
 * Toute lecture est validée par `ContactSchema` (cage IA : aucun accès Firestore
 * direct hors SDK, toute donnée traverse le modèle canonique).
 */
export interface ContactsRepository {
  create(input: ContactCreateInput): Promise<Contact>;
  update(id: string, patch: ContactUpdateInput): Promise<Contact>;
  list(options?: ContactListOptions): Promise<Contact[]>;
  count(options?: ContactCountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: ContactCountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: ContactCountOptions): Promise<number | null>;
  get(id: string): Promise<Contact | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Contact>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Contact>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Contact>;
}

const contactConverter = createConverter<Contact>((raw: DocumentData) => ContactSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk` depuis Firebase Auth), 'system' si absent.
 *   Les Security Rules exigent createdBy/updatedBy == request.auth.uid.
 */
export function createContactsRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ContactsRepository {
  const col = collection(db, CONTACTS_COLLECTION).withConverter(contactConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Contact>(db, col, CONTACTS_COLLECTION, getActorId);

  return {
    async create(input: ContactCreateInput): Promise<Contact> {
      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Zod passe AVANT setDoc : une erreur de saisie ne peut plus persister
      // un document que les lectures suivantes seraient incapables de parser.
      const contact = validateContactCreate(ref.id, input, actor, now);

      // Payload Firestore : champs métier + champs standard auto-remplis.
      // On utilise serverTimestamp() pour l'horodatage faisant autorité.
      // `type` est TOUJOURS écrit (les rules l'exigent à la création) ; les
      // champs d'identité sont conditionnels, une organisation n'a pas de
      // prénom et une personne n'a pas de raison sociale.
      const payload: DocumentData = {
        type: contact.type,
        ...(contact.name !== undefined ? { name: contact.name } : {}),
        ...(contact.firstName !== undefined ? { firstName: contact.firstName } : {}),
        ...(contact.lastName !== undefined ? { lastName: contact.lastName } : {}),
        ...(contact.email !== undefined ? { email: contact.email } : {}),
        ...(contact.phone !== undefined ? { phone: contact.phone } : {}),
        ...(contact.customFields !== undefined ? { customFields: contact.customFields } : {}),
        ...(contact.couleur != null ? { couleur: contact.couleur } : {}),
        ...(contact.photo != null ? { photo: contact.photo } : {}),
        orgId: contact.orgId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Contact);

      return contact;
    },

    async update(id: string, patch: ContactUpdateInput): Promise<Contact> {
      const actor = getActorId();

      // La validation d'un patch exige le document courant : elle conserve les
      // champs non fournis et simule la fusion de customFields avant ecriture.
      const ref = doc(col, id);
      const before = await getDoc(ref);
      if (!before.exists()) throw new Error(`contact ${id} introuvable avant mise à jour.`);
      validateContactUpdate(before.data(), patch, actor, new Date());

      // Seules les clés réellement fournies sont écrites (patch partiel).
      // customFields est eclate en chemins pointes ('customFields.<cle>') pour
      // fusionner cle par cle au lieu de remplacer la map entiere
      // (cf. buildUpdateData ; null = deleteField).
      const data = buildContactUpdateData(sanitizeContactUpdate(patch));
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, CONTACTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec ContactSchema.
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        throw new Error(`contact ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ContactListOptions): Promise<Contact[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: ContactCountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: ContactCountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: ContactCountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Contact | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
